import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Camera, Loader, CheckCircle, AlertCircle, Plus, X, Edit3, Calculator } from 'lucide-react';
import axios from 'axios';
import AutocompleteInput from './AutocompleteInput';
import { PREDEFINED_ITEMS, getItemByName } from '../data/database';

const BillScanner = ({ onItemsScanned }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [scannedText, setScannedText] = useState('');
  const [entryMode, setEntryMode] = useState('scan'); // 'scan', 'manual', or 'proforma'
  const [manualItems, setManualItems] = useState([
    { id: Date.now(), name: '', price: '', quantity: 1 }
  ]);

  const onDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setIsProcessing(true);
    setError(null);
    setScannedText('');

    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await axios.post('/api/ocr', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setScannedText(response.data.text);
        onItemsScanned(response.data.items);
      } else {
        setError('Failed to process the image. Please try again.');
      }
    } catch (err) {
      console.error('OCR Error:', err);
      setError('Failed to scan the bill. Please check your image and try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Manual entry functions
  const addManualItem = () => {
    setManualItems([...manualItems, { 
      id: Date.now() + Math.random(), 
      name: '', 
      price: '', 
      quantity: 1 
    }]);
  };

  const removeManualItem = (id) => {
    if (manualItems.length > 1) {
      setManualItems(manualItems.filter(item => item.id !== id));
    }
  };

  const updateManualItem = (id, field, value) => {
    setManualItems(manualItems.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleItemSelect = (id, selectedItem) => {
    // Handle both object and string selections
    let itemName, itemPrice;
    
    if (typeof selectedItem === 'object' && selectedItem.name) {
      // If it's an object from the predefined items
      itemName = selectedItem.name;
      itemPrice = selectedItem.price.toString();
    } else if (typeof selectedItem === 'string') {
      // If it's a string, try to find it in predefined items
      const predefinedItem = getItemByName(selectedItem);
      if (predefinedItem) {
        itemName = predefinedItem.name;
        itemPrice = predefinedItem.price.toString();
      } else {
        // Custom item, just use the string as name
        itemName = selectedItem;
        itemPrice = '';
      }
    } else {
      return; // Invalid selection
    }
    
    setManualItems(manualItems.map(item => 
      item.id === id ? { 
        ...item, 
        name: itemName, 
        price: itemPrice 
      } : item
    ));
  };

  const handleManualSubmit = () => {
    const validItems = manualItems
      .filter(item => item.name.trim() && item.price && !isNaN(parseFloat(item.price)))
      .map(item => ({
        id: item.id,
        name: item.name.trim(),
        price: parseFloat(item.price),
        quantity: parseInt(item.quantity) || 1,
        total: parseFloat(item.price) * (parseInt(item.quantity) || 1)
      }));

    if (validItems.length === 0) {
      setError('Please add at least one valid item with name and price.');
      return;
    }

    setError('');
    onItemsScanned(validItems);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp']
    },
    multiple: false,
    maxSize: 10 * 1024 * 1024 // 10MB
  });

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Add Your Bill Items</h2>
        <p className="text-gray-600">Choose how you'd like to add your bill items</p>
      </div>

      {/* Mode Toggle */}
      <div className="flex justify-center">
        <div className="bg-gray-100 p-1 rounded-lg flex flex-wrap gap-1">
          <button
            onClick={() => setEntryMode('scan')}
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              entryMode === 'scan'
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Camera className="h-4 w-4 inline mr-2" />
            Scan Receipt
          </button>
          <button
            onClick={() => setEntryMode('manual')}
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              entryMode === 'manual'
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Edit3 className="h-4 w-4 inline mr-2" />
            Manual Entry
          </button>
          <button
            onClick={() => setEntryMode('proforma')}
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              entryMode === 'proforma'
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Calculator className="h-4 w-4 inline mr-2" />
            Pro Forma
          </button>
        </div>
      </div>

      <div className="card max-w-4xl mx-auto">
        {entryMode === 'scan' ? (
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
              ${isDragActive 
                ? 'border-primary-500 bg-primary-50' 
                : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
              }
              ${isProcessing ? 'pointer-events-none opacity-50' : ''}
            `}
          >
            <input {...getInputProps()} />
            
            {isProcessing ? (
              <div className="space-y-4">
                <Loader className="h-12 w-12 text-primary-600 mx-auto animate-spin" />
                <div>
                  <p className="text-lg font-medium text-gray-900">Processing your bill...</p>
                  <p className="text-sm text-gray-500">This may take a few moments</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-center space-x-4">
                  <Upload className="h-12 w-12 text-gray-400" />
                  <Camera className="h-12 w-12 text-gray-400" />
                </div>
                
                <div>
                  <p className="text-lg font-medium text-gray-900">
                    {isDragActive ? 'Drop your bill here' : 'Upload or drag your bill here'}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Supports JPG, PNG, GIF, BMP, WebP (max 10MB)
                  </p>
                </div>
                
                <button className="btn-primary">
                  Choose File
                </button>
              </div>
            )}
          </div>
        ) : entryMode === 'manual' ? (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Add Bill Items Manually</h3>
              <p className="text-sm text-gray-500">Enter each item from your bill</p>
            </div>

            <div className="space-y-3">
              {manualItems.map((item, index) => (
                <div key={item.id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="flex-shrink-0">
                      <span className="w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </span>
                    </div>
                    {manualItems.length > 1 && (
                      <button
                        onClick={() => removeManualItem(item.id)}
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove item"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    <AutocompleteInput
                      value={item.name}
                      onChange={(value) => updateManualItem(item.id, 'name', value)}
                      onSelect={(selectedItem) => handleItemSelect(item.id, selectedItem)}
                      options={PREDEFINED_ITEMS}
                      placeholder="Item name (e.g., Beer, Pizza, Salad)"
                      className="w-full"
                      allowCustom={true}
                      renderOption={(option) => (
                        <div className="flex justify-between items-center">
                          <span>{option.name}</span>
                          <span className="text-sm text-gray-500">€{option.price.toFixed(2)}</span>
                        </div>
                      )}
                    />
                    
                    <div className="flex space-x-3">
                      <div className="flex-1">
                        <label className="block text-xs text-gray-500 mb-1">Quantity</label>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateManualItem(item.id, 'quantity', e.target.value)}
                          placeholder="Qty"
                          min="1"
                          className="input-field w-full"
                        />
                      </div>
                      
                      <div className="flex-1">
                        <label className="block text-xs text-gray-500 mb-1">Price (€)</label>
                        <input
                          type="number"
                          value={item.price}
                          onChange={(e) => updateManualItem(item.id, 'price', e.target.value)}
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          className="input-field w-full"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={addManualItem}
              className="w-full flex items-center justify-center space-x-2 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-primary-400 hover:text-primary-600 transition-colors"
            >
              <Plus className="h-5 w-5" />
              <span>Add another item</span>
            </button>

            <div className="flex justify-center pt-4">
              <button
                onClick={handleManualSubmit}
                className="btn-primary flex items-center space-x-2"
              >
                <CheckCircle className="h-4 w-4" />
                <span>Continue with {manualItems.filter(item => item.name.trim() && item.price).length} items</span>
              </button>
            </div>
          </div>
        ) : entryMode === 'proforma' ? (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Pro Forma Bill</h3>
              <p className="text-sm text-gray-500">Plan your order and estimate costs before you go</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-blue-900 mb-2">How Pro Forma works:</h4>
              <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                <li>Add attendees who will be ordering</li>
                <li>Go through each person and select their items</li>
                <li>Get an estimated bill with individual and group totals</li>
                <li>Perfect for planning ahead or estimating costs</li>
              </ol>
            </div>

            <div className="text-center">
              <button
                onClick={() => onItemsScanned([])} // Pass empty array to trigger Pro Forma flow
                className="btn-primary flex items-center space-x-2 mx-auto"
              >
                <Calculator className="h-5 w-5" />
                <span>Start Pro Forma Bill</span>
              </button>
            </div>
          </div>
        ) : null}

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {scannedText && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <p className="text-green-700 font-medium">Bill scanned successfully!</p>
            </div>
            <details className="mt-2">
              <summary className="text-sm text-green-600 cursor-pointer hover:text-green-700">
                View scanned text
              </summary>
              <pre className="mt-2 text-xs text-gray-600 bg-white p-2 rounded border overflow-auto max-h-32">
                {scannedText}
              </pre>
            </details>
          </div>
        )}
      </div>

      <div className="text-center">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-2xl mx-auto">
          <h3 className="font-medium text-blue-900 mb-2">
            {entryMode === 'scan' ? 'Tips for best OCR results:' : 'Manual entry tips:'}
          </h3>
          <ul className="text-sm text-blue-700 space-y-1">
            {entryMode === 'scan' ? (
              <>
                <li>• Ensure good lighting and clear text</li>
                <li>• Keep the receipt flat and avoid shadows</li>
                <li>• Make sure all text is visible and not cut off</li>
                <li>• Higher resolution images work better</li>
                <li>• If OCR doesn't work well, try manual entry instead</li>
              </>
            ) : (
              <>
                <li>• Enter the exact item name as it appears on the bill</li>
                <li>• Use the quantity field for multiple items of the same type</li>
                <li>• Enter prices without the euro sign (e.g., 12.50)</li>
                <li>• You can add or remove items as needed</li>
                <li>• All items need a name and price to continue</li>
              </>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BillScanner;
